import { Coins } from "lucide-react";
import { Link } from "react-router";

import {
  formatCreditPeriodRemaining,
  formatDisplayCredits,
} from "@/domains/billing/format";
import type { TenantCreditUsage } from "@/domains/billing/types";
import { cn } from "@/shared/lib/utils";

type SidebarCreditMeterProps = {
  usage?: TenantCreditUsage | null;
  /** When false, the meter is informational only and does not link to the profile page. */
  linkToDetails?: boolean;
};

export function SidebarCreditMeter({
  usage,
  linkToDetails = true,
}: SidebarCreditMeterProps) {
  if (!usage) return null;

  const isOverLimit = usage.usageStatus === "over_limit";
  const isWarning =
    usage.usageStatus === "near_limit" ||
    usage.nextAnalysisStatus === "blocked_by_projection";

  const creditsUsed = formatDisplayCredits(usage.creditsUsed);
  const creditsLimit = formatDisplayCredits(usage.creditsLimit);
  const periodRemaining = formatCreditPeriodRemaining(usage.periodEnd);
  const ratio =
    usage.creditsLimit > 0
      ? Math.min(usage.creditsUsed / usage.creditsLimit, 1)
      : 0;

  const ariaLabel = `Credit details: ${creditsUsed} of ${creditsLimit} credits used.`;
  const cardClassName = cn(
    "group/credits block rounded-lg border border-sidebar-border/70 bg-sidebar-accent/40 px-3 py-2.5",
    "group-data-[collapsible=icon]:hidden",
    linkToDetails &&
      "transition-colors duration-150 hover:border-sidebar-primary/30 hover:bg-sidebar-accent",
  );

  const content = (
    <>
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 text-[0.68rem] font-semibold tracking-[0.08em] text-sidebar-foreground/60 uppercase">
          <Coins className="size-3.5" />
          Credits
        </span>
        <span
          className={cn(
            "text-xs font-medium tabular-nums",
            isOverLimit
              ? "text-destructive"
              : isWarning
                ? "text-amber-600"
                : "text-sidebar-foreground/80",
          )}
        >
          {creditsUsed}
          <span className="text-sidebar-foreground/45"> / {creditsLimit}</span>
        </span>
      </div>
      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-sidebar-foreground/10">
        <div
          className={cn(
            "h-full rounded-full transition-[width] duration-500 ease-out",
            isOverLimit
              ? "bg-destructive"
              : isWarning
                ? "bg-amber-500"
                : "bg-sidebar-primary",
          )}
          style={{ width: `${Math.max(ratio * 100, 2)}%` }}
        />
      </div>
      {periodRemaining && (
        <p className="mt-1.5 text-[0.7rem] text-sidebar-foreground/50">
          {periodRemaining}
        </p>
      )}
    </>
  );

  if (!linkToDetails) {
    return (
      <div aria-label={ariaLabel} className={cardClassName}>
        {content}
      </div>
    );
  }

  return (
    <Link
      to={usage.detailsPath}
      aria-label={ariaLabel}
      className={cardClassName}
    >
      {content}
    </Link>
  );
}
